import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { APP_LABELS, isAppKey, type AppKey } from '@qms360/shared';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { PageMessage } from '../AppHome.js';
import { ApiError } from '../../lib/api.js';
import {
  createDistributionList,
  fetchDistributableReports,
  fetchMasterData,
  retire,
  runDistributionList,
  updateDistributionList,
  type DistributableReport,
  type DistributionList,
} from '../../lib/master-data-api.js';

import { ConfirmButton } from '../../components/ConfirmButton.js';
import { localName, t, formatDateTime } from '../../lib/i18n.js';
/**
 * Distribution lists (spec §13.6, §13.8).
 *
 * A list sends one report to its recipients: a digest on a cadence — covering
 * the period that has just closed — or a document when it is approved. It runs
 * as whoever switched it on, and that person's export authority is checked
 * again on every run; a list whose person has lost it stops and says so.
 *
 * "Sent" here means what it means in the outbox: a mail server accepted it.
 */

const CADENCE_LABELS: Record<string, string> = {
  daily: 'Daily',
  fortnightly: 'Fortnightly (1st and 16th)',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  on_approval: 'When approved',
};

const CLOCK_CADENCES = ['daily', 'fortnightly', 'monthly', 'quarterly'] as const;

function when(iso: string | null): string {
  return iso ? formatDateTime(iso, { dateStyle: 'medium', timeStyle: 'short' }) : '—';
}

function message(caught: unknown, fallback: string): string {
  return caught instanceof ApiError ? caught.message : fallback;
}

export function Distribution() {
  const params = useParams<{ app: string }>();
  const app = params.app;
  const valid = Boolean(app) && isAppKey(app);

  const snapshot = useQuery({ queryKey: ['master-data'], queryFn: fetchMasterData, enabled: valid, retry: false });
  const reports = useQuery({
    queryKey: ['distribution-reports', app],
    queryFn: () => fetchDistributableReports(app!),
    enabled: valid,
    retry: false,
  });
  const [editing, setEditing] = useState<DistributionList | 'new' | null>(null);

  if (!valid) return <PageMessage title={t("Unknown application")} body="That application does not exist." />;
  const appKey = app as AppKey;

  const lists = (snapshot.data?.distributionLists ?? []).filter((list) => list.app === appKey);
  const choices = reports.data?.reports ?? [];

  return (
    <div className="min-h-full">
      <AppTopBar app={appKey} />
      <Breadcrumbs app={appKey} trail={['Settings', 'Distribution Lists']} />

      <main className="mx-auto max-w-6xl px-4 pb-16 pt-4">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h1 className="m-0 text-2xl font-semibold">{t("Distribution Lists")}</h1>
          {snapshot.data && reports.data && editing === null && (
            <button
              type="button"
              className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white"
              onClick={() => setEditing('new')}
            >
              {t("New list")}
            </button>
          )}
        </div>
        <p className="mt-1 text-sm text-content-secondary">
          {t("Reports that go out by themselves from")}{' '}{t(APP_LABELS[appKey])}{t(". Email goes through the connection in the")}{' '}
          <Link to={`/${appKey}/settings/integrations`} className="text-ag-purple">
            {t("Integration Cockpit")}
          </Link>
          {t("; until one is set up, messages wait in its outbox, marked not sent. Every run is on the")}{' '}
          <Link to={`/${appKey}/settings/data-sync`} className="text-ag-purple">
            {t("job monitor")}
          </Link>
          .
        </p>

        {(snapshot.isLoading || reports.isLoading) && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}
        {(snapshot.error || reports.error) && (
          <section className="mt-6 rounded-md border border-border bg-tone-info-surface p-6" role="status">
            <h2 className="m-0 text-base font-semibold">{t("Not available to your role")}</h2>
            <p className="mb-0 mt-2 text-sm text-content-secondary">
              {t("Distribution lists need configuration authority in")}{' '}{t(APP_LABELS[appKey])}.
            </p>
          </section>
        )}

        {editing !== null && snapshot.data && (
          <ListForm
            app={appKey}
            list={editing === 'new' ? null : editing}
            choices={choices}
            projects={snapshot.data.projects.rows}
            onDone={() => setEditing(null)}
          />
        )}

        {snapshot.data && reports.data && (
          <section className="mt-6" aria-label={t("Lists")}>
            {lists.length === 0 ? (
              <p className="text-sm text-content-secondary">{t("No distribution lists for")}{' '}{t(APP_LABELS[appKey])}{' '}{t("yet.")}</p>
            ) : (
              <ul className="m-0 list-none space-y-3 p-0">
                {lists.map((list) => (
                  <ListCard
                    key={list.id}
                    list={list}
                    report={choices.find((choice) => choice.key === list.reportKey) ?? null}
                    onEdit={() => setEditing(list)}
                  />
                ))}
              </ul>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

function ListCard({ list, report, onEdit }: { list: DistributionList; report: DistributableReport | null; onEdit: () => void }) {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['master-data'] });

  const toggle = useMutation({
    mutationFn: () => updateDistributionList(list.id, { isEnabled: !list.isEnabled }),
    onSuccess: () => {
      setNotice(null);
      refresh();
    },
    onError: (caught) => setNotice({ tone: 'error', text: message(caught, 'The list could not be changed.') }),
  });
  const run = useMutation({
    mutationFn: () => runDistributionList(list.id),
    onSuccess: ({ run: result }) => {
      refresh();
      const summary = result.summary ?? {};
      const text =
        typeof summary.error === 'string'
          ? summary.error
          : typeof summary.skipped === 'string'
            ? summary.skipped
            : `${result.written} message(s) queued.`;
      setNotice({ tone: result.outcome === 'failed' || typeof summary.error === 'string' ? 'error' : 'ok', text });
    },
    onError: (caught) => setNotice({ tone: 'error', text: message(caught, 'The list could not be run.') }),
  });
  const retireList = useMutation({
    mutationFn: () => retire('distribution-lists', list.id),
    onSuccess: refresh,
    onError: (caught) => setNotice({ tone: 'error', text: message(caught, 'The list could not be retired.') }),
  });

  const recipients = list.recipientUserIds.length + list.recipientEmails.length;

  return (
    <li className="rounded-md border border-border p-4" data-testid={`list-${list.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="m-0 text-base font-semibold">{list.name}</h2>
        <span className="text-sm font-medium">{list.isEnabled ? t("On") : t("Off")}</span>
      </div>
      <p className="mb-0 mt-1 text-sm">
        {report ? report.label : list.reportKey ? list.reportKey : <em>{t("No report chosen")}</em>} ·{' '}
        {CADENCE_LABELS[list.cadence] ?? list.cadence}
        {list.cadence !== 'on_approval' ? t(" at {0}", { 0: list.runTimeLocal }) : ''} · {list.format.toUpperCase()} · {recipients}{' '}
        {t("recipient")}{recipients === 1 ? '' : 's'}
      </p>
      <p className="mb-0 mt-1 text-xs text-content-secondary">
        {list.isEnabled
          ? list.cadence === 'on_approval'
            ? t("Sends what is approved after {0}.", { 0: when(list.lastRunAt) })
            : t("Next run {0}. Last run {1}.", { 0: when(list.nextRunAt), 1: when(list.lastRunAt) })
          : t("Switched off: nothing is sent.")}
        {list.isEnabled && t(" It runs as the person who switched it on, and stops if they can no longer export.")}
      </p>

      {notice && (
        <p
          role={notice.tone === 'error' ? 'alert' : 'status'}
          className="mb-0 mt-2 text-sm"
          style={notice.tone === 'error' ? { color: 'var(--tone-critical)' } : undefined}
        >
          {notice.text}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className="rounded border border-border px-3 py-1 text-sm" onClick={onEdit}>
          {t("Edit")}
        </button>
        <button
          type="button"
          className="rounded border border-border px-3 py-1 text-sm disabled:opacity-60"
          disabled={toggle.isPending}
          onClick={() => toggle.mutate()}
        >
          {list.isEnabled ? t("Switch off") : t("Switch on")}
        </button>
        {list.reportKey && (
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-sm disabled:opacity-60"
            disabled={run.isPending}
            onClick={() => run.mutate()}
          >
            {run.isPending ? t("Running…") : t("Run now")}
          </button>
        )}
        <ConfirmButton
          label={t("Retire")}
          question={t("Retire this list? Nothing more is sent to it; what was sent stays on record.")}
          confirmLabel={t("Yes, retire it")}
          className="rounded border border-border px-3 py-1 text-sm disabled:opacity-60"
          disabled={retireList.isPending}
          onConfirm={() => retireList.mutate()}
        />
      </div>
    </li>
  );
}

function ListForm({
  app,
  list,
  choices,
  projects,
  onDone,
}: {
  app: AppKey;
  list: DistributionList | null;
  choices: DistributableReport[];
  projects: { id: string; code: string; name: string }[];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(list?.name ?? '');
  const [reportKey, setReportKey] = useState(list?.reportKey ?? '');
  const [cadence, setCadence] = useState(list?.cadence ?? 'monthly');
  const [runTimeLocal, setRunTimeLocal] = useState(list?.runTimeLocal ?? '07:00');
  const [format, setFormat] = useState<string>(list?.format ?? 'pdf');
  const [projectId, setProjectId] = useState(list?.parameters.projectId ?? '');
  const [emails, setEmails] = useState((list?.recipientEmails ?? []).join('\n'));
  const [isEnabled, setIsEnabled] = useState(list?.isEnabled ?? false);
  const [error, setError] = useState<string | null>(null);

  const chosen = choices.find((choice) => choice.key === reportKey) ?? null;
  const onApproval = chosen?.onApprovalOnly ?? false;
  const memo = reportKey === 'audit.approval_memo';

  const save = useMutation({
    mutationFn: () => {
      const recipientEmails = emails
        .split(/[\n,;]+/)
        .map((entry) => entry.trim())
        .filter(Boolean);
      const body = {
        name: name.trim(),
        reportKey: reportKey || null,
        cadence: onApproval ? 'on_approval' : cadence === 'on_approval' ? 'monthly' : cadence,
        runTimeLocal,
        format: memo ? 'pdf' : format,
        parameters: projectId ? { projectId } : {},
        recipientEmails,
        isEnabled,
      };
      return list ? updateDistributionList(list.id, body) : createDistributionList({ app, ...body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['master-data'] });
      onDone();
    },
    onError: (caught) => setError(message(caught, 'The list could not be saved.')),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    save.mutate();
  };

  return (
    <form onSubmit={submit} className="mt-6 rounded-md border border-border p-5" aria-label={list ? t("Edit list") : t("New list")}>
      <h2 className="m-0 text-lg font-semibold">{list ? t("Edit {0}", { 0: list.name }) : t("New distribution list")}</h2>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          {t("Name")}
          <input
            className="mt-1 block w-full rounded border border-border px-2 py-1.5"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
        </label>

        <label className="block text-sm">
          {t("Report")}
          <select
            className="mt-1 block w-full rounded border border-border px-2 py-1.5"
            value={reportKey}
            onChange={(event) => setReportKey(event.target.value)}
          >
            <option value="">{t("Choose a report")}</option>
            {choices.map((choice) => (
              <option key={choice.key} value={choice.key}>
                {t(choice.label)}
                {choice.onApprovalOnly ? t(" (sent when approved)") : ''}
              </option>
            ))}
          </select>
        </label>

        {onApproval ? (
          <p className="m-0 self-end text-sm text-content-secondary">
            {t("Sent when it is approved — one message per approval, to everyone on the list.")}
          </p>
        ) : (
          <>
            <div className="text-sm">
              <label className="block">
                {t("How often")}
                <select
                  className="mt-1 block w-full rounded border border-border px-2 py-1.5"
                  value={cadence === 'on_approval' ? 'monthly' : cadence}
                  onChange={(event) => setCadence(event.target.value)}
                  aria-describedby="cadence-help"
                >
                  {CLOCK_CADENCES.map((value) => (
                    <option key={value} value={value}>
                      {t(CADENCE_LABELS[value])}
                    </option>
                  ))}
                </select>
              </label>
              <span id="cadence-help" className="mt-1 block text-xs text-content-secondary">
                {t("Each run covers the period that has just closed: yesterday, the half-month, last month or last quarter.")}
              </span>
            </div>
            <label className="block text-sm">
              {t("At (organisation time)")}
              <input
                type="time"
                className="mt-1 block w-full rounded border border-border px-2 py-1.5"
                value={runTimeLocal}
                onChange={(event) => setRunTimeLocal(event.target.value)}
                required
              />
            </label>
          </>
        )}

        {!memo && (
          <label className="block text-sm">
            {t("Format")}
            <select
              className="mt-1 block w-full rounded border border-border px-2 py-1.5"
              value={format}
              onChange={(event) => setFormat(event.target.value)}
            >
              <option value="pdf">{t("PDF")}</option>
              <option value="xlsx">{t("Excel")}</option>
              <option value="csv">{t("CSV")}</option>
            </select>
          </label>
        )}

        {!memo && (
          <label className="block text-sm">
            {t("Project")}
            <select
              className="mt-1 block w-full rounded border border-border px-2 py-1.5"
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
            >
              <option value="">{t("Every project the list's person can see")}</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.code} — {localName(project)}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="text-sm sm:col-span-2">
          <label className="block">
            {t("Recipients' email addresses")}
            <textarea
              className="mt-1 block w-full rounded border border-border px-2 py-1.5"
              rows={4}
              value={emails}
              onChange={(event) => setEmails(event.target.value)}
              placeholder={t("one address per line")}
              aria-describedby="recipients-help"
            />
          </label>
          <span id="recipients-help" className="mt-1 block text-xs text-content-secondary">
            {t("Someone with a QMS360 account at that address also sees it in the app, in their own language. Anyone else gets the email only, in English, with the report attached.")}
            {list && list.recipientUserIds.length > 0
              ? t(" This list also sends to {0} person(s) chosen by account.", { 0: list.recipientUserIds.length })
              : ''}
          </span>
        </div>

        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input type="checkbox" checked={isEnabled} onChange={(event) => setIsEnabled(event.target.checked)} />
          {t("Switched on — it will run as you, with your export authority, checked again on every run.")}
        </label>
      </div>

      {error && (
        <p role="alert" className="mb-0 mt-3 text-sm" style={{ color: 'var(--tone-critical)' }}>
          {error}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white disabled:opacity-60"
          disabled={save.isPending}
        >
          {save.isPending ? t("Saving…") : t("Save")}
        </button>
        <button type="button" className="rounded border border-border px-3 py-1.5 text-sm" onClick={onDone}>
          {t("Cancel")}
        </button>
      </div>
    </form>
  );
}
